import React, { useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import DateTimePicker from 'components/form/DateTimePicker';
import ReadOnlyField from 'components/form/ReadOnlyField';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  AdminNoticeResult,
  NOTICE_TYPE_KEY,
  NoticeType,
  updateAdminNotice,
} from 'stores/NoticeAdminStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type NoticeUpdateFormHandle = {
  submit: () => void;
  delete: () => void;
  resolve: () => void;
  isEditing: () => boolean;
};

type NoticeUpdateFormProps = {
  notice: AdminNoticeResult;
  users: UserResult[];
  isEditing: boolean;
  onUpdated: () => void;
  onDeleted: () => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

/** 優先度のカラーマッピング */
const priorityColor = (p: number): 'error' | 'warning' | 'info' | 'default' => {
  if (p === 1) return 'error';
  if (p === 2) return 'warning';
  if (p <= 3) return 'info';
  return 'default';
};

const NoticeUpdateForm = React.forwardRef<NoticeUpdateFormHandle, NoticeUpdateFormProps>(
  (
    { notice, users, isEditing, onUpdated, onDeleted, onClose, onEditingChange, setProgress },
    ref,
  ) => {
    const [title, setTitle] = useState(notice.title);
    const [content, setContent] = useState(notice.content);
    const [externalLink, setExternalLink] = useState(notice.externalLink || '');
    const [deadline, setDeadline] = useState(notice.deadline || '');
    const [priority, setPriority] = useState<number>(notice.priority);

    const [errors, setErrors] = useState<Record<string, string>>({});

    React.useEffect(() => {
      setTitle(notice.title);
      setContent(notice.content);
      setExternalLink(notice.externalLink || '');
      setDeadline(notice.deadline || '');
      setPriority(notice.priority);
      setErrors({});
      onEditingChange(false);
    }, [notice]);

    const handleBackToReadOnly = () => {
      setTitle(notice.title);
      setContent(notice.content);
      setExternalLink(notice.externalLink || '');
      setDeadline(notice.deadline || '');
      setPriority(notice.priority);
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!title) newErrors.title = MSG_REQUIRED('タイトル');
      else if (title.length > 300) newErrors.title = MSG_MAX_LENGTH('タイトル', 300);

      if (!content) newErrors.content = MSG_REQUIRED('内容');
      else if (content.length > 10000) newErrors.content = MSG_MAX_LENGTH('内容', 10000);

      if (externalLink && externalLink.length > 500)
        newErrors.externalLink = MSG_MAX_LENGTH('外部リンク', 500);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateAdminNotice({
        noticeId: notice.noticeId,
        title,
        content,
        ...(externalLink && { externalLink }),
        ...(deadline && { deadline }),
        priority,
      })
        .then(() => {
          successDefault('通知を更新しました');
          onUpdated();
        })
        .catch((e: Error) => {
          errorDefault('通知の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    const handleDelete = () => {
      onDeleted();
    };

    const handleResolve = () => {
      // Handled by parent via ConfirmDialog
    };

    useImperativeHandle(ref, () => ({
      submit: handleSave,
      delete: handleDelete,
      resolve: handleResolve,
      isEditing: () => isEditing,
    }));

    // ユーザー情報の表示名を解決
    const getUserName = (userId: string): string => {
      const user = users.find((u) => u.userId === userId);
      return user ? user.userName : userId;
    };

    /** 詳細モード用ヘッダー */
    const renderDetailHeader = (onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {notice.title}
            </Typography>
            <Chip
              label={notice.isResolved ? '解決済' : '未解決'}
              size="small"
              color={notice.isResolved ? 'success' : 'warning'}
              sx={{ fontWeight: 700 }}
            />
            {notice.isSystemNotice && (
              <Chip label="システム通知" size="small" color="default" sx={{ fontWeight: 700 }} />
            )}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
          作成日時: {formatDateTime(notice.createdAt)}
        </Typography>
      </Box>
    );

    /** 編集モード用ヘッダー */
    const renderEditHeader = (onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {notice.title}（編集中）
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
          通知の内容を編集します
        </Typography>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {renderDetailHeader(onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
            {/* 内容 */}
            <Box>
              <ReadOnlyField label="内容" value={notice.content} />
            </Box>

            <Divider />

            {/* 配信設定 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                配信設定
              </Typography>
              <Stack spacing={2}>
                <ReadOnlyField
                  label="期限"
                  value={notice.deadline ? formatDateTime(notice.deadline) : '-'}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    優先度
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={`P${notice.priority}`}
                      size="small"
                      color={priorityColor(notice.priority)}
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Box>
                {(notice.externalLink || notice.internalLink) && (
                  <Box>
                    {notice.externalLink && (
                      <ReadOnlyField label="リンク" value={notice.externalLink} />
                    )}
                    {notice.internalLink && (
                      <ReadOnlyField label="内部リンク" value={notice.internalLink} />
                    )}
                  </Box>
                )}
              </Stack>
            </Box>

            {/* NOTICE: 対象ユーザー表示 */}
            {notice.noticeType === NOTICE_TYPE_KEY.NOTICE &&
              notice.userIds &&
              notice.userIds.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      対象ユーザー（{notice.userIds.length}名）
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {notice.userIds.map((userId) => (
                        <Chip
                          key={userId}
                          label={getUserName(userId)}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

            {/* INFO/IMPORTANT: 解決状況表示 */}
            {(notice.noticeType === NOTICE_TYPE_KEY.INFO ||
              notice.noticeType === NOTICE_TYPE_KEY.IMPORTANT) && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    解決状況
                  </Typography>
                  {notice.resolvedUserIds && notice.resolvedUserIds.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        解決済み（{notice.resolvedUserIds.length}名）
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.5 }}
                      >
                        {notice.resolvedUserIds.map((userId) => (
                          <Chip
                            key={userId}
                            label={getUserName(userId)}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {notice.unresolvedUserIds && notice.unresolvedUserIds.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="warning.main" fontWeight={600}>
                        未解決（{notice.unresolvedUserIds.length}名）
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.5 }}
                      >
                        {notice.unresolvedUserIds.map((userId) => (
                          <Chip
                            key={userId}
                            label={getUserName(userId)}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ borderRadius: 1.5 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {renderEditHeader(handleBackToReadOnly)}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.title}
                helperText={errors.title}
                slotProps={{ htmlInput: { maxLength: 300 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="内容"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                size="small"
                required
                multiline
                rows={6}
                error={!!errors.content}
                helperText={errors.content || `${content.length}/10000`}
                slotProps={{ htmlInput: { maxLength: 10000 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* 配信設定 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              配信設定
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    期限
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <DateTimePicker
                      label=""
                      mode="end"
                      value={deadline}
                      onChange={setDeadline}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    優先度（1: 最高 〜 5: 最低）
                  </Typography>
                  <ToggleButtonGroup
                    value={priority}
                    exclusive
                    onChange={(_, v) => {
                      if (v !== null) setPriority(v);
                    }}
                    size="small"
                    sx={{ mt: 0.5, display: 'flex' }}
                  >
                    {[1, 2, 3, 4, 5].map((v) => (
                      <ToggleButton
                        key={v}
                        value={v}
                        sx={{
                          flex: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      >
                        {v}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Stack>
              <TextField
                label="リンク"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                fullWidth
                size="small"
                placeholder="https://..."
                error={!!errors.externalLink}
                helperText={errors.externalLink}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              {notice.internalLink && (
                <TextField
                  label="内部リンク"
                  value={notice.internalLink}
                  fullWidth
                  size="small"
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    );
  },
);

NoticeUpdateForm.displayName = 'NoticeUpdateForm';

export default NoticeUpdateForm;
